import React from "react";
import PropTypes from "prop-types";
import { CModal, CModalBody, CModalHeader, CModalTitle, CModalFooter, CButton } from "@coreui/react";
import { useTranslation } from "react-i18next";
import { FaExclamationTriangle, FaQuestionCircle } from "react-icons/fa";
import "./ConfirmationModal.css";

const ConfirmationModal = ({ 
  isOpen, 
  onConfirm, 
  onCancel, 
  title, 
  message, 
  confirmButtonText, 
  cancelButtonText,
  isFirstConfirmation = true,
  isDangerous = false
}) => {
  const { t } = useTranslation();

  const getIconColor = () => {
    if (isFirstConfirmation) return "#ffc107"; // Warning yellow
    return isDangerous ? "#dc3545" : "#28a745"; // Red for dangerous, green for safe
  };

  const getIcon = () => {
    if (isFirstConfirmation) {
      return <FaQuestionCircle size={48} color={getIconColor()} />;
    }
    return <FaExclamationTriangle size={48} color={getIconColor()} />;
  };

  const getConfirmButtonColor = () => {
    if (isFirstConfirmation) return "warning";
    return isDangerous ? "danger" : "success";
  };

  const getModalHeaderClass = () => {
    if (isFirstConfirmation) return "confirmation-modal-header-warning";
    return isDangerous ? "confirmation-modal-header-danger" : "confirmation-modal-header-success";
  };

  return (
    <CModal
      visible={isOpen}
      onClose={onCancel}
      size="md"
      className="confirmation-modal"
    >
      <CModalHeader className={getModalHeaderClass()}>
        <CModalTitle className="confirmation-modal-title">
          {title}
        </CModalTitle>
      </CModalHeader>
      <CModalBody className="confirmation-modal-body">
        <div className="confirmation-content">
          <div className="confirmation-icon">
            {getIcon()}
          </div>
          <div className="confirmation-message">
            <p>{message}</p>
            {!isFirstConfirmation && (
              <div className="warning-text">
                <strong>{t("thisActionCannotBeUndoneEasily")}</strong>
              </div>
            )}
          </div>
        </div>
      </CModalBody>
      <CModalFooter className="confirmation-modal-footer">
        <CButton
          color="secondary"
          onClick={onCancel}
          className="cancel-button"
        >
          {cancelButtonText}
        </CButton>
        <CButton
          color={getConfirmButtonColor()}
          onClick={onConfirm}
          className="confirm-button"
        >
          {confirmButtonText}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmButtonText: PropTypes.string.isRequired,
  cancelButtonText: PropTypes.string.isRequired,
  isFirstConfirmation: PropTypes.bool,
  isDangerous: PropTypes.bool,
};

export default ConfirmationModal;